import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh,
  CloudUpload,
  Undo,
  CheckCircle,
  Error,
  Warning,
  Visibility,
  Timeline,
  ModelTraining,
} from '@mui/icons-material';

import { CreditRiskAPI, formatPercentage } from '../services/api';
import { ModelVersion, ModelInfo } from '../types';

interface ModelManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const ModelManagement: React.FC<ModelManagementProps> = ({ onNotification }) => {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [versions, setVersions] = useState<{ [key: string]: ModelVersion[] }>({});
  const [loading, setLoading] = useState(false);
  const [promoteDialog, setPromoteDialog] = useState<{
    open: boolean;
    model?: ModelInfo;
    version?: string;
  }>({ open: false });
  const [rollbackDialog, setRollbackDialog] = useState<{
    open: boolean;
    model?: ModelInfo;
  }>({ open: false });
  const [promoteForm, setPromoteForm] = useState({
    stage: 'Staging',
    description: ''
  });
  const [rollbackForm, setRollbackForm] = useState({
    targetVersion: '',
    reason: ''
  });

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    try {
      // Fetch models with metrics via the shared client (correct origin + auth token)
      const data = await CreditRiskAPI.getModelsWithMetrics();

      if (data && data.models) {
        setModels(data.models);

        // Map versions from the response
        const versionMap: { [key: string]: ModelVersion[] } = {};
        data.models.forEach((model: any) => {
          if (model.versions && model.versions.length > 0) {
            versionMap[model.name] = model.versions;
          } else {
            versionMap[model.name] = [];
          }
        });
        setVersions(versionMap);
      }
    } catch (error) {
      onNotification('Failed to load models', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteModel = async () => {
    if (!promoteDialog.model || !promoteDialog.version) return;

    try {
      const response = await CreditRiskAPI.promoteModel(
        promoteDialog.model.name,
        promoteDialog.version,
        promoteForm.stage,
        promoteForm.description
      );

      if (response.success) {
        onNotification(`Model promoted to ${promoteForm.stage} successfully`, 'success');
        setPromoteDialog({ open: false });
        setPromoteForm({ stage: 'Staging', description: '' });
        loadModels(); // Refresh model list
      } else {
        onNotification(response.error?.message || 'Failed to promote model', 'error');
      }
    } catch (error) {
      onNotification('Failed to promote model', 'error');
    }
  };

  const handleRollbackModel = async () => {
    if (!rollbackDialog.model) return;

    try {
      const response = await CreditRiskAPI.rollbackModel(
        rollbackDialog.model.name,
        rollbackForm.targetVersion,
        rollbackForm.reason
      );

      if (response.success) {
        onNotification('Model rollback completed successfully', 'success');
        setRollbackDialog({ open: false });
        setRollbackForm({ targetVersion: '', reason: '' });
        loadModels(); // Refresh model list
      } else {
        onNotification(response.error?.message || 'Failed to rollback model', 'error');
      }
    } catch (error) {
      onNotification('Failed to rollback model', 'error');
    }
  };

  const handleAutoPromote = async (modelName: string) => {
    try {
      const response = await CreditRiskAPI.autoPromoteModel(modelName, 0.70, 'auc');

      if (response.success) {
        onNotification('Auto-promotion completed successfully', 'success');
        loadModels(); // Refresh model list
      } else {
        onNotification(response.error?.message || 'Auto-promotion failed', 'error');
      }
    } catch (error) {
      onNotification('Failed to auto-promote model', 'error');
    }
  };

  const handleReloadModels = async () => {
    try {
      const response = await CreditRiskAPI.reloadModels(true);

      if (response.success) {
        onNotification('Models reloaded from MLflow successfully', 'success');
        loadModels(); // Refresh model list
      } else {
        onNotification(response.error?.message || 'Failed to reload models', 'error');
      }
    } catch (error) {
      onNotification('Failed to reload models', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'production': return 'success';
      case 'staging': return 'warning';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'production': return <CheckCircle />;
      case 'staging': return <Warning />;
      case 'archived': return <Error />;
      default: return <ModelTraining />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Gestión de Modelos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadModels}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleReloadModels}
            disabled={loading}
          >
            Recargar desde MLflow
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Gestione las versiones de modelos, promociones y rollbacks para los modelos de riesgo crediticio.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {models.map((model) => (
            <Grid item xs={12} key={model.model_id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        {model.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {model.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getStatusIcon(model.status)}
                        label={model.status}
                        color={getStatusColor(model.status)}
                        size="medium"
                      />
                      <Typography variant="caption" color="text.secondary">
                        v{model.version}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Model Metrics */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h6" color="primary">
                          {model.metrics?.auc_roc ? formatPercentage(model.metrics?.auc_roc) : 'N/A'}
                        </Typography>
                        <Typography variant="caption">AUC-ROC</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h6" color="primary">
                          {model.metrics?.precision ? formatPercentage(model.metrics?.precision) : 'N/A'}
                        </Typography>
                        <Typography variant="caption">Precisión</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      {/* <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h6" color="primary">
                          {model.metrics?.gini ? formatPercentage(model.metrics?.gini) : 'N/A'}
                        </Typography>
                        <Typography variant="caption">Gini</Typography>
                      </Box> */}
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h6" color="primary">
                          {model.algorithm}
                        </Typography>
                        <Typography variant="caption">Algoritmo</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Version History */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        Historial de Versiones ({versions[model.name]?.length || 0})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Versión</TableCell>
                              <TableCell>Etapa</TableCell>
                              <TableCell>Creado</TableCell>
                              <TableCell>Estado</TableCell>
                              <TableCell>Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {versions[model.name]?.map((version) => (
                              <TableRow key={version.version}>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace">
                                    {version.version}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={version.current_stage}
                                    size="small"
                                    color={getStatusColor(version.current_stage)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {new Date(version.creation_timestamp).toLocaleDateString('es-CR')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={version.status}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Ver detalles">
                                      <IconButton size="small">
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {version.current_stage !== 'Production' && (
                                      <Tooltip title="Promover">
                                        <IconButton
                                          size="small"
                                          onClick={() => setPromoteDialog({
                                            open: true,
                                            model,
                                            version: version.version
                                          })}
                                        >
                                          <CloudUpload fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<CloudUpload />}
                    onClick={() => handleAutoPromote(model.name)}
                    disabled={loading}
                  >
                    Auto-Promocionar
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Undo />}
                    onClick={() => setRollbackDialog({
                      open: true,
                      model
                    })}
                    color="warning"
                  >
                    Rollback
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Timeline />}
                    onClick={() => {
                      // Navigate to analysis tab for this model
                      const modelType = model.name === 'Loan Default Predictor' ? 'loan_default' : 'scorecard';
                      window.location.hash = `#analysis-${modelType}`;
                      onNotification(`Ver métricas para ${model.name}`, 'info');
                    }}
                  >
                    Ver Métricas
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Promote Dialog */}
      <Dialog open={promoteDialog.open} onClose={() => setPromoteDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Promover Modelo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Promover {promoteDialog.model?.name} v{promoteDialog.version} a una nueva etapa
          </Typography>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Etapa de destino</InputLabel>
              <Select
                value={promoteForm.stage}
                onChange={(e) => setPromoteForm({ ...promoteForm, stage: e.target.value })}
                label="Etapa de destino"
              >
                <MenuItem value="Staging">Staging</MenuItem>
                <MenuItem value="Production">Production</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción (opcional)"
              value={promoteForm.description}
              onChange={(e) => setPromoteForm({ ...promoteForm, description: e.target.value })}
              placeholder="Descripción de los cambios o motivo de la promoción..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialog({ open: false })}>
            Cancelar
          </Button>
          <Button
            onClick={handlePromoteModel}
            variant="contained"
            disabled={!promoteForm.stage}
          >
            Promover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rollback Dialog */}
      <Dialog open={rollbackDialog.open} onClose={() => setRollbackDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Rollback de Modelo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Realizar rollback del modelo {rollbackDialog.model?.name} a una versión anterior
          </Typography>
          <Alert severity="warning" sx={{ my: 2 }}>
            Esta acción revertirá el modelo en producción a la versión especificada. Use con precaución.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Versión de destino</InputLabel>
              <Select
                value={rollbackForm.targetVersion}
                onChange={(e) => setRollbackForm({ ...rollbackForm, targetVersion: e.target.value })}
                label="Versión de destino"
              >
                {versions[rollbackDialog.model?.name || '']?.map((version) => (
                  <MenuItem key={version.version} value={version.version}>
                    v{version.version} ({version.current_stage})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Motivo del rollback *"
              value={rollbackForm.reason}
              onChange={(e) => setRollbackForm({ ...rollbackForm, reason: e.target.value })}
              placeholder="Explique el motivo del rollback (requerido para auditoría)..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialog({ open: false })}>
            Cancelar
          </Button>
          <Button
            onClick={handleRollbackModel}
            variant="contained"
            color="warning"
            disabled={!rollbackForm.targetVersion || !rollbackForm.reason.trim()}
          >
            Realizar Rollback
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelManagement;